import React, { useReducer, useEffect, useMemo, useState } from "react";
import ReactModal from "react-modal";
import { ImClipboard } from "react-icons/im";
import { VscServerProcess } from "react-icons/vsc";
import { TbPackageExport } from "react-icons/tb";
import { FaJira } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import "./../styles/statusBar.css";
import { parseDate } from "../utils/parseDate";
import TrackItComponent from "./TrackItComponent";
import CreateJiraComponent from "./JiraTabComponent";
import EmailingComponent from "./EmailingTabComponent";
import ModalContentDetails from "./ModalContentComponent";
import AppSystemTrackPage from "./AppSystemTrackPage";
import OrderTrackingPage from "./OrderTrackingPage";
import useFetchAppSystemTrack from "../hooks/useFetchAppSystemTrack";
import {
  getStepFromStatus,
  getStatusText,
  getCssClassName,
  getIconColor,
  getFlowLinkType,
} from "../utils/Miscellaneous";

// Initial state and reducer function
const initialState = {
  modalIsOpen: false,
  currentTransaction: null,
  trackItActive: false,
  trackItParams: null,
  activeTab: null,
  visibleTabs: {
    logs: false,
    jira: false,
    email: false,
  },
  flowLinkType: "",
  inboundStatusFlag: false,
  outboundStatusFlag: false,
  sentTransaction: null,
  bookingNumber: null,
  partyId: null,
  systemName: null,
  orderNo: null,
  orderSourceType: null,
  customerName: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_MODAL":
      return {
        ...state,
        modalIsOpen: action.payload.modalIsOpen,
        currentTransaction: action.payload.currentTransaction,
      };
    case "SET_TRACK_IT":
      return {
        ...state,
        trackItActive: action.payload.trackItActive,
        trackItParams: action.payload.trackItParams,
      };
    case "SET_ACTIVE_TAB":
      return { ...state, activeTab: action.payload };
    case "SET_VISIBLE_TABS":
      return {
        ...state,
        visibleTabs: {
          ...state.visibleTabs,
          [action.payload.tab]: !state.visibleTabs[action.payload.tab],
        },
      };
    case "SET_FLOW_LINK_TYPE":
      return { ...state, flowLinkType: action.payload };
    case "SET_STATUS_FLAGS":
      return {
        ...state,
        inboundStatusFlag: action.payload.inboundStatusFlag,
        outboundStatusFlag: action.payload.outboundStatusFlag,
      };
    case "SET_SENT_TRANSACTION":
      return {
        ...state,
        sentTransaction: action.payload.sentTransaction,
        systemName: action.payload.systemName,
        bookingNumber: action.payload.bookingNumber,
        partyId: action.payload.partyId,
      };
    case "RESET_SENT_TRANSACTION":
      return { ...state, sentTransaction: null };
    default:
      return state;
  }
}

const TransactionDetailBox = ({ transactions = [] }) => {
  const transactionArray = useMemo(
    () => (Array.isArray(transactions) ? transactions : [transactions]),
    [transactions]
  );
  const { loading, error, fetchSystemTrackInfo, fetchOrderInfo } =
    useFetchAppSystemTrack();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [appSystemTrackData, setAppSystemTrackData] = useState({});
  const [systemName, setSystemName] = useState("");
  const [bookingNumber, setBookingNumber] = useState("");
  const [partyId, setPartyId] = useState("");
  const [linkType, setFlowLinkType] = useState("");
  const [flowLinkType, setflowLinkType] = useState("");
  const navigate = useNavigate();
  const [isAppSystemTrackModalOpen, setAppSystemTrackModalOpen] =
    useState(false);
  const [isTrackItModalOpen, setTrackItModalOpen] = useState(false);
  const [flowLinkTypeChangeCount, setFlowLinkTypeChangeCount] = useState(0);
  const [isTrackButtonVisible, setIsTrackButtonVisible] = useState(true);
  const [orderTrackingInfo, setOrderTrackingInfo] = useState({});
  const [orderNo, setOrderNumber] = useState("");
  const [orderSourceType, setOrderSourceType] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [showOrderTrackingModalOpen, setShowOrderTrackingModalOpen] =
    useState(false);
  const [showOrderTrackingButton, setShowOrderTrackingButton] = useState(false);

  ReactModal.setAppElement("#root"); // Ensure your app root element is defined correctly

  useEffect(() => {
    if (!transactionArray || transactionArray.length === 0) return;

    const outboundStatusFlag = transactionArray.some(
      (transaction) =>
        transaction.messageFlowType === "OUTBOUND" &&
        getStatusText(transaction.boomiStatus) === "received" &&
        transaction.inboundFlowInd === "Y"
    );

    const inboundStatusFlag = transactionArray.some(
      (transaction) =>
        transaction.messageFlowType === "INBOUND" &&
        getStatusText(transaction.boomiStatus) === "sent" &&
        transaction.outboundFlowInd === "Y"
    );

    const newFlowType = outboundStatusFlag
      ? "Inbound"
      : inboundStatusFlag
      ? "Outbound"
      : "unknown";

    // Prevent redundant updates
    if (newFlowType !== flowLinkType) {
      setFlowLinkType(newFlowType);
      setFlowLinkTypeChangeCount((prevCount) => prevCount + 1);
    }

    if (flowLinkTypeChangeCount >= 2) {
      setIsTrackButtonVisible(false);
    }

    // Dispatch updates
    dispatch({
      type: "SET_STATUS_FLAGS",
      payload: { inboundStatusFlag, outboundStatusFlag },
    });

    dispatch({
      type: "SET_FLOW_LINK_TYPE",
      payload: newFlowType,
    });

    const foundTransaction = transactionArray.find(
      (transaction) =>
        getStepFromStatus(transaction.boomiStatus) === "Sent" &&
        (transaction.boomiStatus === "Sent-TMF" ||
          transaction.boomiStatus === "Sent-C2C")
    );

    if (foundTransaction) {
      const system = foundTransaction.boomiStatus.split("-")[1] || "";
      const partyId = getPartyId(foundTransaction.incomingData, system);

      setSystemName(system);
      setBookingNumber(foundTransaction.documentIdentifier || "");
      setPartyId(partyId);

      dispatch({
        type: "SET_SENT_TRANSACTION",
        payload: {
          sentTransaction: foundTransaction,
          systemName: system,
          bookingNumber: foundTransaction.documentIdentifier || "",
          partyId: partyId,
          orderNo: orderNo,
          orderSourceType: orderSourceType,
          customerName: customerName,
        },
      });
    } else {
      dispatch({ type: "RESET_SENT_TRANSACTION" });
    }
  }, [transactionArray]); // Only `transactionArray` is necessary as a dependency

  useEffect(() => {
    if (bookingNumber && partyId && systemName) {
      const fetchAppSystemTrackInfo = async () => {
        try {
          const data = await fetchSystemTrackInfo(
            bookingNumber,
            partyId,
            systemName
          );
          if (data) {
            console.log("AppSystemTrackInfo to set:", data);
            setAppSystemTrackData(data);
          }
        } catch (error) {
          console.error("Error fetching AppSystemTrackInfo: ", error);
        }
      };
      fetchAppSystemTrackInfo();
    }
  }, [bookingNumber, partyId, systemName, fetchSystemTrackInfo]);

  useEffect(() => {
    if (orderNo && orderSourceType && customerName) {
      const fetchOrderTrackingInfo = async () => {
        try {
          const data = await fetchOrderInfo(
            orderNo,
            orderSourceType,
            customerName
          );
          if (data) {
            console.log("Order Info: ", data);
            setOrderTrackingInfo(data);
          }
        } catch (error) {
          console.error("Error fetching OrderTrackingInfo: ", error);
        }
      };
      fetchOrderTrackingInfo();
    }
  }, [orderNo, orderSourceType, customerName, fetchOrderInfo]);

  const getPartyId = (incomingData, systemName) => {
    let partyIdCust = null;
    try {
      const cleanedString = incomingData
        .replace("<![CDATA[", "")
        .replace("]]>", "");
      const parser = new DOMParser();

      console.log(`systemName in getPartyId : ${systemName}`);

      switch (systemName) {
        case "TMF":
          const xmlDoc = parser.parseFromString(cleanedString, "text/xml");
          partyIdCust =
            xmlDoc.getElementsByTagName("PartyID_Cust")[0]?.textContent || "";
          break;

        case "C2C":
          const customerNumberRegex = new RegExp("POH\\s([\\S]+)\\s.+", "g");
          const match = customerNumberRegex.exec(cleanedString);
          partyIdCust = match ? match[1] : "";
          break;

        default:
          console.warn(`Unknown systemName: ${systemName}`);
          break;
      }
    } catch (error) {
      console.error(
        "Error parsing incomingData in getPartyId function:",
        error
      );
    }
    return partyIdCust;
  };

  console.log("show tracking button after", showOrderTrackingButton);

  const openModal = (transaction) => {
    dispatch({
      type: "SET_MODAL",
      payload: { modalIsOpen: true, currentTransaction: transaction },
    });
  };

  const closeModal = () => {
    dispatch({
      type: "SET_MODAL",
      payload: { modalIsOpen: false, currentTransaction: null },
    });
  };

  const handleTabClick = (tab) => {
    dispatch({ type: "SET_VISIBLE_TABS", payload: { tab } });
    dispatch({ type: "SET_ACTIVE_TAB", payload: tab });
  };

  const handleTrackItClick = () => {
    console.log("TrackIt Clicked");
    setTrackItModalOpen(true);

    // Determine the customer name based on the flow type (inbound or outbound)
    const customerName = (() => {
      if (state.flowLinkType === "Outbound") {
        const sentTransaction = transactionArray.find(
          (transaction) => getStatusText(transaction.boomiStatus) === "sent"
        );
        if (sentTransaction) {
          setSystemName(sentTransaction.boomiStatus.split("-")[1] || "");
          console.log("Outbound sentTransaction", sentTransaction);
          return sentTransaction?.senderName;
        }
      } else if (state.flowLinkType === "Inbound") {
        const receivedTransaction = transactionArray.find(
          (transaction) => getStatusText(transaction.boomiStatus) === "received"
        );
        if (receivedTransaction) {
          setSystemName(receivedTransaction.boomiStatus.split("-")[1] || "");
          console.log("Inbound receivedTransaction", receivedTransaction);
          return receivedTransaction?.receiverName;
        }
      }
      return ""; // Fallback if no transaction found
    })();

    console.log("Customer Name: ", customerName);

    // Dispatch action to activate TrackIt with the correct customer name and other parameters
    dispatch({
      type: "SET_TRACK_IT",
      payload: {
        trackItActive: true,
        trackItParams: {
          customerName: customerName, // Dynamically set customer name
          flowLinkType: state.flowLinkType, // Use the current flowLinkType (Inbound/Outbound)
          referenceIdentifier:
            state.flowLinkType === "Inbound"
              ? transactionArray[0]?.inboundReference
              : transactionArray[0]?.documentIdentifier || "", // Default to first transaction's identifier
        },
      },
    });
  };

  const getOrderType = (identifier) => {
    if (identifier.includes("WarehouseShippingOrder")) {
      return "SO"; // Sales Order
    } else if (
      identifier.includes("PurchaseOrder") ||
      identifier.includes("AdvanceShipNotice")
    ) {
      return "PO"; // Purchase Order
    } else {
      return "Unknown";
    }
  };

  const handleTrackOrderClick = (transaction) => {
    console.log("Track Order Clicked with this data: ", transaction);

    const newOrderNo = transaction.documentIdentifier;
    const newOrderSourceType = getOrderType(transaction.documentType);
    const newCustomerName = transaction.senderName;

    setOrderNumber(newOrderNo);
    setOrderSourceType(newOrderSourceType);
    setCustomerName(newCustomerName);

    if (newOrderSourceType !== "Unknown") {
      fetchOrderInfo(newOrderNo, newOrderSourceType, newCustomerName);
      setShowOrderTrackingModalOpen(true);
    }
  };

  const handleSentClick = () => {
    setAppSystemTrackModalOpen(true);
  };

  const closeAppSystemTrackModal = () => {
    setAppSystemTrackModalOpen(false);
  };

  const closeOrderTrackingModal = () => {
    setShowOrderTrackingModalOpen(false);
  };

  const closeTrackItModal = () => {
    setTrackItModalOpen(false);
    dispatch({
      type: "SET_TRACK_IT",
      payload: { trackItActive: false, trackItParams: null },
    });
    dispatch({
      type: "SET_MODAL",
      payload: { modalIsOpen: false, currentTransaction: null },
    });
  };

  const steps = ["Received", "Processing", "Sent"];
  const icons = [ImClipboard, VscServerProcess, TbPackageExport];

  const isDisabled =
    transactionArray.length === 0 ||
    transactionArray.some((transaction) =>
      ["Complete", "Incomplete"].includes(transaction.boomiStatus)
    );

  const showTrackButton = !!state.sentTransaction;

  const isErrorStatus = state.sentTransaction?.boomiStatus?.includes("Error") || !!state.sentTransaction?.errorReason?.trim();
  const isReceiverSofteon = transactionArray[0]?.receiverName === "SOFTEON";
  const isPOorSO = ["PO", "SO"].includes(getOrderType(transactionArray[0]?.documentType));

  return (
    <div className="transaction-group-container">
      {!state.trackItActive && transactionArray.length > 0 && (
        <div className="status-bar-container">
          <>
            <div>
              {/* <Link to={`/transaction/${transactionArray[0]?.messageGroupId}/status`} className='transaction-link'> */}
              <div className="transaction-status-header">
                Transactions Status{" "}
              </div>
              {/* </Link> */}
            </div>
            <div className="status-bar-top">
              <p>
                {transactionArray[0].documentType}:{" "}
                {transactionArray[0].documentIdentifier}
                <div>
                  ReferenceType: {transactionArray[0].documentReferenceType}
                </div>
              </p>
              <p>Sender: {transactionArray[0].senderName}</p>
            </div>
            <div className="status-bar">
              {steps.map((step, stepIndex) => (
                <div
                  key={stepIndex}
                  className={`step-container ${getIconColor(
                    step,
                    transactionArray
                  )}`}
                >
                  <div
                    className={`status-step ${getCssClassName(
                      step,
                      transactionArray
                    )}`}
                  >
                    <div className="icon">
                      <button
                        onClick={() =>
                          openModal(
                            transactionArray.find(
                              (transaction) =>
                                getStepFromStatus(transaction.boomiStatus) ===
                                step
                            )
                          )
                        }
                        disabled={isDisabled}
                      >
                        {React.createElement(icons[stepIndex], { size: 28 })}
                      </button>
                    </div>
                    <p>{step}</p>
                  </div>
                  {stepIndex < steps.length - 1 && (
                    <div
                      className={`step-line ${getCssClassName(
                        step,
                        transactionArray
                      )} ${getIconColor(step, transactionArray)}`}
                    ></div>
                  )}
                </div>
              ))}
            </div>
            {state.modalIsOpen && state.currentTransaction && (
              <ModalContentDetails
                transactionArray={transactionArray}
                closeModal={closeModal}
                currentFlowStep={getStepFromStatus(
                  state.currentTransaction.boomiStatus
                )}
                transaction={state.currentTransaction}
              />
            )}
          </>
        </div>
      )}
      {!state.trackItActive && (
        <div className="tabs">
          <button onClick={() => handleTabClick("logs")} title="Logs">
            <ImClipboard size={40} />
          </button>
          <button onClick={() => handleTabClick("jira")} title="Jira">
            <FaJira size={20} />
          </button>
          <button onClick={() => handleTabClick("email")} title="Email">
            <MdEmail size={20} />
          </button>
          {(state.inboundStatusFlag || state.outboundStatusFlag) && (
            <button onClick={handleTrackItClick}>Track {linkType}</button>
          )}
          {state.sentTransaction && (
            <button onClick={handleSentClick} hidden={!showTrackButton}>
              Track {state.sentTransaction?.boomiStatus.split("-")[1] || ""}
            </button>
          )}
          {((isReceiverSofteon && !isErrorStatus) && isPOorSO) && (
              <button
                onClick={() => handleTrackOrderClick(transactionArray[0])}
                title="Track Order"
              >
                Track Order
              </button>
            )}
        </div>
      )}

      {state.trackItActive && state.trackItParams && (
        <TrackItComponent
          customerName={state.trackItParams.customerName}
          flowLinkType={state.trackItParams.flowLinkType}
          referenceIdentifier={state.trackItParams.referenceIdentifier}
          onClose={closeTrackItModal} // Pass the close function
        />
      )}
      {!state.trackItActive && state.visibleTabs.logs && (
        <div className="transaction-logs">
          {transactionArray
            .sort((a, b) => {
              const statusPriority = {
                Received: 1,
                Processing: 2,
                Sent: 3,
                "Received-Error": 1,
                "Translated-Error": 2,
                "Sent-Error": 3,
              };

              // First, sort by the priority of the status
              const statusA =
                statusPriority[getStepFromStatus(a.boomiStatus)] || 99; // Default to low priority if unknown status
              const statusB =
                statusPriority[getStepFromStatus(b.boomiStatus)] || 99;

              if (statusA !== statusB) {
                return statusA - statusB;
              }

              // If statuses are the same, sort by DateTime (ascending order)
              return parseDate(a.DateTime) - parseDate(b.DateTime);
            })
            .map((transaction, index) => (
              <div key={index} className="transaction-log-item">
                {parseDate(transaction.DateTime)?.toLocaleString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  fractionalSecondDigits: 3,
                }) || "Invalid Date"}{" "}
                - Boomi &nbsp;
                {getStepFromStatus(transaction.boomiStatus)}&nbsp; EDI &nbsp;
                {transaction.documentType} {transaction.documentReferenceType}{" "}
                {transaction.documentIdentifier}&nbsp;
                {getStatusText(transaction.boomiStatus) === "sent"
                  ? `to ${transaction.receiverName}`
                  : `from ${transaction.senderName}`}{" "}
                &nbsp;
                {/Received-Error|Translated-Error|Sent-Error|Sent-C2C-Error|Sent-TMF-Error|Sent-Footprint-Error|Sent-TP-Error|Sent-Softeon-Error/.test(
                  transaction.boomiStatus
                )
                  ? "with errors"
                  : ""}
              </div>
            ))}
        </div>
      )}
      {state.activeTab === "jira" && state.visibleTabs.jira && (
        <div className="tab-content">
          <CreateJiraComponent transactions={transactions} />
        </div>
      )}
      {state.activeTab === "email" && state.visibleTabs.email && (
        <div className="tab-content">
          <EmailingComponent transactions={transactionArray} />
        </div>
      )}
      {/* Modal for AppSystemTrackPage */}
      <ReactModal
        isOpen={isAppSystemTrackModalOpen && bookingNumber && systemName}
        onRequestClose={closeAppSystemTrackModal}
        contentLabel="App System Track Details"
      >
        {bookingNumber && partyId && systemName ? (
          <AppSystemTrackPage
            bookingNumber={bookingNumber}
            partyId={partyId}
            systemName={systemName}
            appSystemTrackData={appSystemTrackData}
          />
        ) : (
          <p>No tracking data available</p>
        )}
        <button
          onClick={closeAppSystemTrackModal}
          className="modal-close-button"
        >
          Close
        </button>
      </ReactModal>
      {/* Modal for OrderTrackingPage */}
      <ReactModal
        isOpen={
          showOrderTrackingModalOpen &&
          orderNo &&
          orderSourceType &&
          customerName
        }
        onRequestClose={closeOrderTrackingModal}
        contentLabel="Order Tracking Details"
      >
        {orderNo && orderSourceType && customerName ? (
          <OrderTrackingPage
            OrderNo={orderNo}
            OrderSourceType={orderSourceType}
            CustomerName={customerName}
            orderTrackingInfo={orderTrackingInfo}
          />
        ) : (
          <p>No tracking data available</p>
        )}
        <button
          onClick={closeOrderTrackingModal}
          className="modal-close-button"
        >
          Close
        </button>
      </ReactModal>
    </div>
  );
};

export default TransactionDetailBox;
